import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  bootstrapPerf,
  clearPerf,
  disablePerf,
  enablePerf,
  getPerfEntries,
  isPerfEnabled,
  recordPerf,
  subscribePerf,
  type PerfEntry,
} from "@/lib/perf-hud";
import { queryClient } from "@/lib/query-client";

/**
 * Dev-only floating Perf HUD overlay.
 *
 * Activation:
 *   - 5-finger tap toggles on/off (works on iPhone & Android).
 *   - `?perf=1` URL param enables; `?perf=0` disables.
 *   - Setting persists in localStorage so it survives reloads.
 *
 * Render cost when DISABLED: only a passive touchstart listener.
 * Render cost when ENABLED: minimal - appends to a ring buffer
 * capped at 80 entries; HUD repaints only on new entries.
 */
export function PerfHud() {
  const [enabled, setEnabled] = useState<boolean>(() => isPerfEnabled());
  const [entries, setEntries] = useState<PerfEntry[]>(() => getPerfEntries());
  const [minimized, setMinimized] = useState(false);
  const location = useLocation();
  const lastRouteRef = useRef<{ path: string; t: number } | null>(null);

  // Bootstrap once (?perf=… flag + window.__elefaPerf).
  useEffect(() => {
    bootstrapPerf();
    setEnabled(isPerfEnabled());
  }, []);

  // 5-finger tap toggles enable/disable globally (native gesture).
  useEffect(() => {
    function onTouch(e: TouchEvent) {
      if (e.touches.length >= 5) {
        if (isPerfEnabled()) {
          disablePerf();
          setEnabled(false);
        } else {
          enablePerf();
          setEnabled(true);
        }
      }
    }
    window.addEventListener("touchstart", onTouch, { passive: true });
    return () => window.removeEventListener("touchstart", onTouch);
  }, []);

  // Subscribe to entry stream only while enabled.
  useEffect(() => {
    if (!enabled) return;
    const unsub = subscribePerf((next) => setEntries([...next]));
    return () => {
      unsub();
    };
  }, [enabled]);

  // Measure route transition wall time.
  useEffect(() => {
    if (!enabled) return;
    const now = performance.now();
    const prev = lastRouteRef.current;
    if (prev && prev.path !== location.pathname) {
      recordPerf({
        kind: "route",
        label: `${prev.path} → ${location.pathname}`,
        ms: Math.round(now - prev.t),
      });
    }
    lastRouteRef.current = { path: location.pathname, t: now };
  }, [enabled, location.pathname]);

  // Hook into React Query cache to time fetches.
  useEffect(() => {
    if (!enabled) return;
    const cache = queryClient.getQueryCache();
    const starts = new Map<string, number>();
    const unsub = cache.subscribe((event) => {
      const q = event?.query;
      if (!q) return;
      const key = q.queryHash;
      const state = q.state;
      if (state.fetchStatus === "fetching" && !starts.has(key)) {
        starts.set(key, performance.now());
      } else if (state.fetchStatus === "idle" && starts.has(key)) {
        const ms = Math.round(performance.now() - (starts.get(key) ?? 0));
        starts.delete(key);
        const label = Array.isArray(q.queryKey)
          ? q.queryKey
              .map((k) => (typeof k === "string" ? k : JSON.stringify(k)))
              .join(":")
              .slice(0, 60)
          : String(q.queryKey).slice(0, 60);
        recordPerf({
          kind: "query",
          label,
          ms,
          detail: state.status,
        });
      }
    });
    return () => unsub();
  }, [enabled]);

  // PerformanceObserver: long tasks (>50ms) blocking the main thread.
  useEffect(() => {
    if (!enabled) return;
    if (typeof PerformanceObserver === "undefined") return;
    let obs: PerformanceObserver | null = null;
    try {
      obs = new PerformanceObserver((list) => {
        for (const e of list.getEntries()) {
          recordPerf({
            kind: "longtask",
            label: "long task",
            ms: Math.round(e.duration),
          });
        }
      });
      obs.observe({ entryTypes: ["longtask"] });
    } catch {
      /* longtask unsupported on Safari - silently skip */
    }
    return () => {
      try {
        obs?.disconnect();
      } catch {
        /* ignore */
      }
    };
  }, [enabled]);

  if (!enabled) return null;

  const reversed = [...entries].reverse();

  return (
    <div
      style={{
        position: "fixed",
        right: 8,
        bottom: 96,
        zIndex: 99999,
        width: minimized ? 64 : 280,
        maxHeight: minimized ? 32 : "40vh",
        background: "rgba(0,0,0,0.82)",
        color: "#0f0",
        fontFamily: "ui-monospace, Menlo, monospace",
        fontSize: 10,
        lineHeight: 1.3,
        borderRadius: 8,
        padding: 6,
        boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
        pointerEvents: "auto",
        overflow: "hidden",
        backdropFilter: "blur(6px)",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 6,
          alignItems: "center",
          color: "#fff",
          marginBottom: minimized ? 0 : 4,
        }}
      >
        <strong style={{ flex: 1, fontSize: 10 }}>
          ⚡ PERF {entries.length}
        </strong>
        <button
          onClick={() => setMinimized((m) => !m)}
          style={btnStyle}
          aria-label="minimize"
        >
          {minimized ? "▢" : "-"}
        </button>
        {!minimized && (
          <>
            <button onClick={clearPerf} style={btnStyle} aria-label="clear">
              ⟳
            </button>
            <button
              onClick={() => {
                disablePerf();
                setEnabled(false);
              }}
              style={btnStyle}
              aria-label="disable"
            >
              ×
            </button>
          </>
        )}
      </div>
      {!minimized && (
        <div style={{ overflowY: "auto", maxHeight: "34vh" }}>
          {reversed.length === 0 && (
            <div style={{ color: "#888" }}>
              Tap around - entries appear here.
            </div>
          )}
          {reversed.map((e) => (
            <div key={e.id} style={{ display: "flex", gap: 6 }}>
              <span style={{ color: colorFor(e.kind), width: 40 }}>
                {tagFor(e.kind)}
              </span>
              <span style={{ color: msColor(e.ms), width: 44 }}>
                {e.ms != null ? `${e.ms}ms` : ""}
              </span>
              <span
                style={{
                  color: "#cfc",
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {e.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const btnStyle: React.CSSProperties = {
  background: "rgba(255,255,255,0.15)",
  color: "#fff",
  border: 0,
  borderRadius: 4,
  padding: "1px 6px",
  fontSize: 10,
  cursor: "pointer",
};

function tagFor(k: PerfEntry["kind"]) {
  switch (k) {
    case "route":
      return "ROUTE";
    case "query":
      return "QUERY";
    case "longtask":
      return "BLOCK";
    case "mark":
      return "MARK";
  }
}
function colorFor(k: PerfEntry["kind"]) {
  switch (k) {
    case "route":
      return "#7cf";
    case "query":
      return "#fc7";
    case "longtask":
      return "#f77";
    case "mark":
      return "#ccc";
  }
}
function msColor(ms?: number) {
  if (ms == null) return "#888";
  if (ms < 100) return "#7f7";
  if (ms < 300) return "#ff7";
  return "#f77";
}

export default PerfHud;
